import { useState, useEffect, useMemo } from 'react';
import Papa from 'papaparse';
import { useWizard } from '../context/WizardCtx';

/**
 * Estrutura interna CSV row interface
 */
export interface EstruturaInternaRow {
  codigoCliente: string;
  nomeCliente: string;
  nomeVendedor: string;
  limiteDia: string | number; // Can be string for "infinito" or number
  limiteVolume: string | number; // Can be string for "infinito" or number
}

/**
 * Grouped data for dropdowns
 */
export interface EstruturaInternaData {
  vendedores: string[];
  clientes: EstruturaInternaRow[];
  clientesByVendedor: Record<string, EstruturaInternaRow[]>;
  uniqueClientNames: string[]; // Unique client names across all vendedores
  getClientesByCodigo: (codigo: string) => EstruturaInternaRow[];
  getClientesByName: (nome: string) => EstruturaInternaRow[];
  getCodigosByClienteName: (nome: string) => string[];
  getClienteByNameAndCodigo: (nome: string, codigo: string) => EstruturaInternaRow | undefined;
  getLimitesByVendedor: (vendedor: string) => { limiteDia: Set<string | number>; limiteVolume: Set<string | number> };
}

/**
 * Hook for loading and managing estrutura_interna.csv data
 * Uses cadastro data from wizard context when available, otherwise loads from CSV
 */
export const useEstruturaInterna = () => {
  const { data: wizardData } = useWizard();
  const [csvData, setCsvData] = useState<EstruturaInternaRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load CSV data on mount
  useEffect(() => {
    const loadEstruturaInterna = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch('/estrutura_interna.csv');
        if (!response.ok) {
          throw new Error(`Failed to load CSV: ${response.statusText}`);
        }

        const csvText = await response.text();
        
        // Parse CSV with Papa Parse
        const result = Papa.parse<string[]>(csvText, {
          skipEmptyLines: true,
          delimiter: ',',
          header: false
        });

        if (result.errors.length > 0) {
          console.warn('CSV parsing warnings:', result.errors);
        }

        // Skip header row and convert to structured data
        const rows = result.data.slice(1);
        const parsedData: EstruturaInternaRow[] = rows.map((row, index) => {
          if (row.length < 5) {
            console.warn(`Row ${index + 2} has insufficient columns:`, row);
            return null;
          }

          const [codigoCliente, nomeCliente, nomeVendedor, limiteDia, limiteVolume] = row;
          
          return {
            codigoCliente: codigoCliente?.trim() || '',
            nomeCliente: nomeCliente?.trim() || '',
            nomeVendedor: nomeVendedor?.trim() || '',
            limiteDia: parseLimiteValue(limiteDia?.trim()),
            limiteVolume: parseLimiteValue(limiteVolume?.trim())
          };
        }).filter((row): row is EstruturaInternaRow => row !== null);

        setCsvData(parsedData);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error loading CSV';
        console.error('Error loading estrutura_interna.csv:', err);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadEstruturaInterna();
  }, []);

  // Helper function to parse limite values
  const parseLimiteValue = (value: string): string | number => {
    if (!value) return '';
    
    // Check if it's a numeric value
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      return numValue;
    }
    
    // Return as string for non-numeric values like "infinito"
    return value;
  };

  // Determine which data to use: cadastro from wizard or original CSV
  const activeData = useMemo((): EstruturaInternaRow[] => {
    if (wizardData.cadastro && wizardData.cadastro.length > 0) {
      // Use cadastro data from wizard (Step 0)
      return wizardData.cadastro.map(row => ({
        codigoCliente: row.codigoCliente,
        nomeCliente: row.nomeCliente,
        nomeVendedor: row.nomeVendedor,
        limiteDia: row.limiteDia,
        limiteVolume: row.limiteVolume
      }));
    } else {
      // Use original CSV data
      return csvData;
    }
  }, [wizardData.cadastro, csvData]);

  // Memoized processed data for dropdowns
  const processedData = useMemo((): EstruturaInternaData => {
    // Get unique vendedores
    const vendedores = Array.from(new Set(
      activeData
        .map(row => row.nomeVendedor)
        .filter(vendedor => vendedor)
    )).sort();

    // Group clientes by vendedor
    const clientesByVendedor: Record<string, EstruturaInternaRow[]> = {};
    activeData.forEach(row => {
      if (row.nomeVendedor) {
        if (!clientesByVendedor[row.nomeVendedor]) {
          clientesByVendedor[row.nomeVendedor] = [];
        }
        clientesByVendedor[row.nomeVendedor].push(row);
      }
    });

    // Sort clientes within each vendedor group
    Object.keys(clientesByVendedor).forEach(vendedor => {
      clientesByVendedor[vendedor].sort((a, b) => a.nomeCliente.localeCompare(b.nomeCliente));
    });

    // Helper functions
    const getClientesByCodigo = (codigo: string): EstruturaInternaRow[] => {
      return activeData.filter(row => row.codigoCliente === codigo);
    };

    const getClientesByName = (nome: string): EstruturaInternaRow[] => {
      return activeData.filter(row => row.nomeCliente === nome);
    };

    const getCodigosByClienteName = (nome: string): string[] => {
      return activeData
        .filter(row => row.nomeCliente === nome)
        .map(row => row.codigoCliente);
    };

    const getClienteByNameAndCodigo = (nome: string, codigo: string): EstruturaInternaRow | undefined => {
      return activeData.find(row => row.nomeCliente === nome && row.codigoCliente === codigo);
    };

    const getLimitesByVendedor = (vendedor: string) => {
      const clientesDoVendedor = clientesByVendedor[vendedor] || [];
      const limiteDia = new Set(clientesDoVendedor.map(c => c.limiteDia));
      const limiteVolume = new Set(clientesDoVendedor.map(c => c.limiteVolume));
      
      return { limiteDia, limiteVolume };
    };

    return {
      vendedores,
      clientes: activeData,
      clientesByVendedor,
      uniqueClientNames: Array.from(new Set(activeData.map(row => row.nomeCliente))),
      getClientesByCodigo,
      getClientesByName,
      getCodigosByClienteName,
      getClienteByNameAndCodigo,
      getLimitesByVendedor
    };
  }, [activeData]);

  return {
    data: processedData,
    isLoading,
    error,
    rawData: csvData // Return original CSV data for CadastroTableStep
  };
}; 